import {
  SMARTPLEX_MASTER_NUMBER,
  PTO_MASTER_NUMBER,
  PTO_BASE_NUMBER,
  N_MLOGIC,
  N_MLOGIC2,
  O_MLOGIC,
  N_SLOTCOPY,
  O_SLOTCOPY,
  N_MAP,
  O_MAP,
  N_PLC,
  O_TIMER,
  N_TIMER,
  N_APP_DTC,
  N_APP_FAULT,
  O_FAULT,
  N_FUEL_LEVEL_CFG,
  N_FUEL_LEVEL_DATA,
  N_LIGHTMONITOR,
  N_PREFLIGHT,
  N_PREFLIGHT2,
  N_PREFLIGHT3,
  N_PRIORITY,
  S_CANSLOTS,
  S_MAPSLOTS,
  S_MEMORY,
  S_MLOGICSLOTS,
  S_SLOTINITSLOTS,
  S_TIMERSLOTS,
  N_SLOTINIT,
  O_SLOTINIT,
  N_SSADDRESS,
  N_STALKSWITCH,
  N_SUBCONFIG,
  N_SUBDATA,
  N_TEMP_LEVEL_CFG,
  N_TEMP_LEVEL_DATA,
  N_USER_DTC,
  N_POWERDOWN,
  N_Modal,
  N_VIN,
  N_VERSION,
  N_CANSIZE,
  N_RPSIZE,
  N_RPLOC,
  N_EFFORT_CONTROL_DATA,
  N_ANALOG_SENSOR_8BIT_DATA,
  N_ANALOG_SENSOR_16BIT_DATA,
  N_ANALOG_SENSOR_32BIT_DATA,
  N_EFFORT_CONTROL_CFG,
  N_ANALOG_SENSOR_8BIT_CFG,
  N_ANALOG_SENSOR_16BIT_CFG,
  N_ANALOG_SENSOR_32BIT_CFG,
  N_USER_FAULT,
  RP_SERVICE_DESCRIPTION_MAX_LENGTH,
  CHEC_MAX_VALUE,
  O_CAN,
  CARRY_ONLY,
  stringMaxVal,
  VEHICLE_SUBTYPES,
  PTO,
  SMARTPLEX,
  RP,
} from 'utils/constants'
import { Set } from 'immutable'
import strings from 'utils/strings'
import { prop, pick, propOr, compose } from 'ramda'

/* 
DTNA reference parameter numbers have the format 26-XXXXX-YYY. The
"26-XXXXX" is often referred to as the base number, and denotes a
vehicle platform and a functional area. The "-YYY" is sometimes called
the suffix, and identifies a specific unique reference parameter
within the base.

CHEC part numbers are 10 digits. Their "base number" is contained
within the first 5 digits of their release number and relate to the
"XXXXX" from the base number of the parameter from which they were
duplicated.

It is important to note that only one reference parameter or CHEC part
number of any given base number can be on a truck.  Adding a new one
to a truck in service will replace the matching original.

https://dtna.visualstudio.com/CHEC/_wiki/wikis/CHEC.wiki?wikiVersion=GBwikiMaster&pagePath=%2FCHEC%20domain%20knowledge%2FReference%20parameter%20base%20numbers
*/
export const DTNA_RP_ROOT_NUMBER = 26
const DTNA_RP_ROOT = `${DTNA_RP_ROOT_NUMBER}-`
const RP_BASE_LEN = 5

// const m2Prefixes = new Set(['01', '20'])
const b2Prefixes = new Set(['02', '03'])
const rvmPrefixes = new Set(['04'])

export const getPlatformFromRP = (rp) => {
  if (!rp) {
    return VEHICLE_SUBTYPES.M2
  }
  const baseNumber = getRPBaseNumber(rp)
  const platformId = baseNumber.substr(0, 2)

  if (b2Prefixes.has(platformId)) {
    return VEHICLE_SUBTYPES.BUS
  }

  if (rvmPrefixes.has(platformId)) {
    return VEHICLE_SUBTYPES.RV
  }

  return VEHICLE_SUBTYPES.M2
}

const isDTNARPNumber = (num = '') => num.startsWith(DTNA_RP_ROOT)

export const rpIsDTNARPNumber = compose(
  isDTNARPNumber,
  propOr('', 'refParameterNumber')
)

const dtnaBaseNumber = (num = '') =>
  num.substring(DTNA_RP_ROOT.length, DTNA_RP_ROOT.length + RP_BASE_LEN)

const getDTNARPBaseNumber = compose(
  dtnaBaseNumber,
  propOr('', 'refParameterNumber')
)

const rpBaseNumberFromRelease = (number = '') =>
  number.substring(0, RP_BASE_LEN)

const getRPBaseNumberFromRelease = compose(
  rpBaseNumberFromRelease,
  propOr('', 'release')
)

const getBaseNumber = (refParameterNumber, release) => {
  if (isDTNARPNumber(refParameterNumber))
    return dtnaBaseNumber(refParameterNumber)

  return rpBaseNumberFromRelease(release)
}

export const getRPBaseNumber = (rp) => {
  if (rpIsDTNARPNumber(rp)) {
    return getDTNARPBaseNumber(rp)
  }
  return getRPBaseNumberFromRelease(rp)
}

export const compareRpsBaseNumbers = (
  rpNumOne,
  releaseNumOne,
  rpNumTwo,
  releaseNumTwo
) => {
  const rpOneBaseNum = getBaseNumber(rpNumOne, releaseNumOne)
  const rpTwoBaseNum = getBaseNumber(rpNumTwo, releaseNumTwo)
  return rpOneBaseNum === rpTwoBaseNum
}

export const compareRpBaseNumber = (rp1, rp2) =>
  getRPBaseNumber(rp1) === getRPBaseNumber(rp2)

export const rpIsSmartplex = (rp) =>
  rp && rp.masterNumber && rp.masterNumber === SMARTPLEX_MASTER_NUMBER

export const rpIsPTO = (rp) =>
  (rp && rp.masterNumber && rp.masterNumber === PTO_MASTER_NUMBER) ||
  (rp && rp.release && getRPBaseNumberFromRelease(rp) === PTO_BASE_NUMBER)

export const getRPType = (rp) => {
  if (rpIsPTO(rp)) return PTO

  if (rpIsSmartplex(rp)) return SMARTPLEX

  return RP
}

export const sPTypeConstToDisplay = new Map([
  [N_MLOGIC, strings.nmlogic_title],
  [N_MLOGIC2, strings.nmlogic2_title],
  [O_MLOGIC, strings.omlogic_title],
  [N_SLOTCOPY, strings.nslotcopy_title],
  [O_SLOTCOPY, strings.oslotcopy_title],
  [N_MAP, strings.nmap_title],
  [O_MAP, strings.omap_title],
  [N_PLC, strings.nplc_title],
  [N_TIMER, strings.ntimer_title],
  [O_TIMER, strings.otimer_title],
  [N_APP_DTC, strings.nappdtc_title],
  [N_APP_FAULT, strings.nappfault_title],
  [O_FAULT, strings.ofault_title],
  [N_FUEL_LEVEL_CFG, strings.nfuel_level_cfg_title],
  [N_FUEL_LEVEL_DATA, strings.nfuel_level_data_title],
  [N_LIGHTMONITOR, strings.nlightmonitor_title],
  [N_PREFLIGHT, strings.npreflight_title],
  [N_PREFLIGHT2, strings.npreflight2_title],
  [N_PREFLIGHT3, strings.npreflight3_title],
  [N_PRIORITY, strings.npriority_title],
  [S_CANSLOTS, strings.scanslots_title],
  [S_MAPSLOTS, strings.smapslots_title],
  [S_MEMORY, strings.smemory_title],
  [S_MLOGICSLOTS, strings.smlogicslots_title],
  [S_SLOTINITSLOTS, strings.sslotinitslots_title],
  [S_TIMERSLOTS, strings.stimerslots_title],
  [N_SLOTINIT, strings.nslotinit_title],
  [O_SLOTINIT, strings.oslotinit_title],
  [N_SSADDRESS, strings.nssaddress_title],
  [N_STALKSWITCH, strings.nstalkswitch_title],
  [N_SUBCONFIG, strings.subconfigs_title],
  [N_SUBDATA, strings.nsubdata_title],
  [N_TEMP_LEVEL_CFG, strings.ntemp_level_cfg_title],
  [N_TEMP_LEVEL_DATA, strings.ntemp_level_data_title],
  [N_USER_DTC, strings.nuserdtc_title],
  [N_POWERDOWN, strings.npowerdown_title],
  [N_Modal, strings.nModal_title],
  [N_VIN, strings.nvin_title],
  [N_VERSION, strings.nversion_title],
  [N_CANSIZE, strings.ncansize_title],
  [N_RPSIZE, strings.nrpsize_title],
  [N_RPLOC, strings.nrploc_title],
  [N_EFFORT_CONTROL_DATA, strings.neffortcontroldata_title],
  [N_ANALOG_SENSOR_8BIT_DATA, strings.nanalogsensor8bitdata_title],
  [N_ANALOG_SENSOR_16BIT_DATA, strings.nanalogsensor16bitdata_title],
  [N_ANALOG_SENSOR_32BIT_DATA, strings.nanalogsensor32bitdata_title],
  [N_EFFORT_CONTROL_CFG, strings.neffortcontrolcfg_title],
  [N_ANALOG_SENSOR_8BIT_CFG, strings.nanalogsensor8bitcfg_title],
  [N_ANALOG_SENSOR_16BIT_CFG, strings.nanalogsensor16bitcfg_title],
  [N_ANALOG_SENSOR_32BIT_CFG, strings.nanalogsensor32bitcfg_title],
  [N_USER_FAULT, strings.nuserfault_title],
])

export const isRPValidToSave = (rp) => {
  const {
    status,
    original,
    removedSupportParameters,
    compatibilityModules,
    serviceDescription,
    itemDescription,
    uri,
    release,
    refParameterNumber,
    removeOnSaveOrCancelEdit,
    supportParameters,
  } = rp

  const errors = []

  if (status === 'Active') {
    errors.push(strings.cannot_edit_active_rp)
  }

  const originalSPUris = Set(
    original.supportParameters.map((param) => param.uri)
  )
  const spsToAdd = supportParameters
    .filter(
      (param) => !originalSPUris.has(param.uri) && !param.deleteOnCancelEdit
    )
    .map((sp) => ({ uri: prop('uri', sp) }))

  const spsToRemove = removedSupportParameters.map((sp) => ({
    uri: prop('uri', sp),
  }))

  const originalCompatibleModuleUris = original.compatibilityModules.map(
    (param) => param.uri
  )
  const currentCompatibleModuleUris = compatibilityModules.map(
    (param) => param.uri
  )

  const compatibilityModulesToAdd = currentCompatibleModuleUris
    .filter((module) => originalCompatibleModuleUris.indexOf(module) === -1)
    .map(pick(['uri']))

  const compatibilityModulesToRemove = originalCompatibleModuleUris
    .filter((module) => currentCompatibleModuleUris.indexOf(module) === -1)
    .map(pick(['uri']))

  if (!serviceDescription) {
    errors.push(strings.service_description_error)
  } else if (serviceDescription.length > RP_SERVICE_DESCRIPTION_MAX_LENGTH) {
    errors.push(strings.service_description_length_error)
  }

  const dataPacket = {
    itemDescription,
    serviceDescription,
    uri,
    refParameterNumber,
    release,
    add: spsToAdd.concat(compatibilityModulesToAdd),
    remove: spsToRemove
      .concat(compatibilityModulesToRemove)
      .concat(removeOnSaveOrCancelEdit),
  }

  return errors.length > 0
    ? {
        errors,
        dataPacket: false,
      }
    : {
        errors: false,
        dataPacket,
      }
}

export const getRpInputs = (spList, currentSupportParameter) => {
  if (!spList || spList.length === 0) return { rpInputs: [], rpOutputs: [] }

  const nativeOutputs = [].concat(
    spList.filter((param) => {
      // build native outputs first
      if (param.supportParameterType === N_TIMER) return true // all N_TIMERS are ok
      if (
        param.supportParameterType === N_MAP &&
        param.priority !== stringMaxVal &&
        ['2', '5', '6', '7', '8', '11'].includes(param.type)
      )
        return true // include only N_MAPS that match this criteria
      return false
    }),
    !currentSupportParameter.supportParameterType.includes('SLOTCOPY')
      ? [
          {
            // add the CARRY_ONLY option
            uri: String(CHEC_MAX_VALUE),
            description: CARRY_ONLY,
          },
        ]
      : []
  )
  const nativeInputs = spList.filter((param) =>
    [N_MLOGIC2, N_MLOGIC, N_TIMER, N_MAP].includes(param.supportParameterType)
  ) // native inputs can use any of these types

  if (
    [O_MLOGIC, O_SLOTCOPY].includes(
      currentSupportParameter.supportParameterType
    )
  ) {
    // if we're looking at O_MLOGIC or O_SLOTCOPY
    return {
      rpOutputs: [].concat(
        nativeOutputs,
        spList.filter((param) => {
          if (param.supportParameterType === O_TIMER) return true // all O_TIMERs are valid outputs
          if (param.supportParameterType === O_MAP) {
            if (param.type === 'C') return true // O_MAPs of type circuit are valid
            if (param.type === 'M' && param.priority !== stringMaxVal)
              return true // O_MAPS of type CAN message with a priority not 65535 are valid
            if (param.type === 'SI') return true // O_MAPs of type switch indicator are valid outputs
          }
          return false
        })
      ),
      rpInputs: [].concat(
        nativeInputs,
        spList.filter((param) =>
          [O_MLOGIC, O_TIMER, O_MAP].includes(param.supportParameterType)
        ) // all SPs of these types are valid for inputs
      ),
    }
  }

  return {
    rpInputs: nativeInputs,
    rpOutputs: nativeOutputs,
  }
}

export const spTypeToDisplayStrDic = {
  [O_MAP]: strings.map_possibly_plural,
  [N_MAP]: strings.map_possibly_plural,
  [N_TIMER]: strings.timer_possibly_plural,
  [N_MLOGIC]: strings.logic_possibly_plural,
  [N_MLOGIC2]: strings.logic_possibly_plural,
  [N_PLC]: strings.plc_possibly_plural,
  [O_MLOGIC]: strings.logic_possibly_plural,
  [O_CAN]: strings.can_possibly_plural,
}

export const isSPValid = (sp, currentlyLinkedSPs) => {
  if (sp.supportParameterType === O_MAP) {
    const linkedOmaps = currentlyLinkedSPs.filter(
      ({ supportParameterType, type, uri }) => {
        const isOmap = supportParameterType === O_MAP
        const isNotCurrentSP = uri !== sp.uri
        return isNotCurrentSP && isOmap
      }
    )
    if (
      sp.type === 'C' &&
      linkedOmaps.find((omap) => omap.physical_slot === sp.physical_slot)
    ) {
      return {
        localError: strings.circuit_duplication_error,
        globalError: strings.editing_param_type_error(
          spTypeToDisplayStrDic[sp.supportParameterType]
        ),
        type: O_MAP,
      }
    }
    return linkedOmaps.find((omap) => omap.logical_slot === sp.logical_slot)
      ? {
          localError: strings.slot_duplication_error,
          globalError: strings.editing_param_type_error(
            spTypeToDisplayStrDic[sp.supportParameterType]
          ),
          type: O_MAP,
        }
      : null
  }

  if (
    [N_TIMER, N_MLOGIC, N_MLOGIC2, N_PLC, N_MAP, N_SLOTCOPY].includes(
      sp.supportParameterType
    )
  ) {
    const linkedSPs = currentlyLinkedSPs.filter(
      ({ supportParameterType, uri }) => {
        const isSameType = supportParameterType === sp.supportParameterType
        const isNotCurrentSP = uri !== sp.uri
        return isNotCurrentSP && isSameType
      }
    )
    return linkedSPs.find((s) => s.rowid === sp.rowid)
      ? {
          localError: strings.rowid_duplication_error,
          globalError: strings.editing_param_type_error(
            spTypeToDisplayStrDic[sp.supportParameterType]
          ),
          type: sp.supportParameterType,
        }
      : null
  }

  if (O_MLOGIC === sp.supportParameterType) {
    const linkedSPs = currentlyLinkedSPs.filter(
      ({ supportParameterType, uri }) => {
        const isSameType = supportParameterType === sp.supportParameterType
        const isNotCurrentSP = uri !== sp.uri
        return isNotCurrentSP && isSameType
      }
    )
    return linkedSPs.find((s) => s.mLogicSlotName === sp.mLogicSlotName)
      ? {
          localError: strings.slot_duplication_error,
          globalError: strings.editing_param_type_error(
            spTypeToDisplayStrDic[sp.supportParameterType]
          ),
          type: sp.supportParameterType,
        }
      : null
  }

  if (O_CAN === sp.supportParameterType) {
    const linkedSPs = currentlyLinkedSPs.filter(
      ({ supportParameterType, uri }) => {
        const isSameType = supportParameterType === sp.supportParameterType
        const isNotCurrentSP = uri !== sp.uri
        return isNotCurrentSP && isSameType
      }
    )
    return linkedSPs.find((s) => s.can_slot === sp.can_slot)
      ? {
          localError: strings.slot_duplication_error,
          globalError: strings.editing_param_type_error(
            spTypeToDisplayStrDic[sp.supportParameterType]
          ),
          type: sp.supportParameterType,
        }
      : null
  }

  return false
}
